import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight, Phone } from 'lucide-react';
import { generateGeneralWhatsAppLink } from '../data/products';
import company from '../data/company';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'COLLECTIONS', path: '/collections' },
    { name: 'EXPORT', path: '/export' },
    { name: 'DISTRIBUTORSHIP', path: '/distributorship' },
    { name: 'GLOBAL CORPORATE', path: '/global-business' },
    { name: 'OUR PROCESS', path: '/process' },
    { name: 'ABOUT', path: '/about' },
    { name: 'CONTACT', path: '/contact' }
  ];

  return (
    <>
      {/* ── MAIN NAVBAR ── */}
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1000,
          background: isScrolled
            ? 'rgba(255,255,255,0.98)'
            : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(15,23,42,0.07)',
          boxShadow: isScrolled ? '0 4px 24px rgba(15,23,42,0.08)' : 'none',
          transition: 'box-shadow 0.3s ease, background 0.3s ease'
        }}
      >
        <div
          className="container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            height: 'var(--nav-height)'
          }}
        >
          {/* ── Logo ── */}
          <Link
            to="/"
            style={{ display: 'flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none' }}
          >
            <motion.img
              src="/Rayyandenimlogo.jpeg"
              alt="RAYYAN DENIM"
              className="navbar-logo"
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 350 }}
              style={{
                height: isScrolled ? '64px' : '78px',
                width: 'auto',
                objectFit: 'contain',
                borderRadius: '6px',
                transition: 'height 0.3s ease'
              }}
            />
          </Link>

          {/* ── Desktop Nav Links ── */}
          <nav
            className="desktop-nav"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flex: 1,
              justifyContent: 'center'
            }}
          >
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '13.5px',
                    fontWeight: isActive ? 800 : 700,
                    letterSpacing: '0.08em',
                    color: isActive ? 'var(--accent-magenta)' : 'var(--text-main)',
                    position: 'relative',
                    padding: '8px 14px',
                    transition: 'color 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {link.name}
                  {isActive && (
                    <motion.span
                      layoutId="activeNav"
                      style={{
                        position: 'absolute',
                        bottom: 0, left: '14px', right: '14px',
                        height: '2.5px',
                        background: 'linear-gradient(90deg, var(--accent-purple), var(--accent-magenta))',
                        borderRadius: '2px'
                      }}
                      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* ── Right: CTA + Hamburger ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <motion.a
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              href={generateGeneralWhatsAppLink('International Buyer')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary desktop-cta"
              style={{ padding: '9px 16px', fontSize: '11.5px', gap: '6px' }}
            >
              ENQUIRY
              <ArrowUpRight size={13} />
            </motion.a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="mobile-menu-toggle"
              aria-label="Toggle menu"
              style={{
                background: 'none',
                border: '1px solid var(--color-border)',
                borderRadius: '7px',
                color: 'var(--text-main)',
                cursor: 'pointer',
                width: '38px',
                height: '38px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* ── Mobile Drawer ── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(15,23,42,0.55)',
                zIndex: 1050,
                backdropFilter: 'blur(2px)'
              }}
            />

            {/* Drawer panel */}
            <motion.div
              key="drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'fixed',
                top: 0, right: 0,
                width: 'min(300px, 88vw)',
                height: '100vh',
                background: '#ffffff',
                zIndex: 1100,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-6px 0 32px rgba(15,23,42,0.18)',
                overflowY: 'auto'
              }}
            >
              {/* Drawer header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid var(--color-border)',
                minHeight: '68px'
              }}>
                <img
                  src="/Rayyandenimlogo.jpeg"
                  alt="RAYYAN DENIM"
                  style={{ height: '44px', objectFit: 'contain', borderRadius: '5px' }}
                />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    background: '#f8fafc', border: '1px solid var(--color-border)',
                    borderRadius: '6px', cursor: 'pointer',
                    width: '34px', height: '34px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text-main)'
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Nav links */}
              <div style={{ flex: 1, padding: '20px' }}>
                {navLinks.map((link) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <Link
                      key={link.name}
                      to={link.path}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontFamily: 'var(--font-heading)',
                        fontSize: '15px',
                        fontWeight: isActive ? 800 : 700,
                        letterSpacing: '0.04em',
                        color: isActive ? 'var(--accent-magenta)' : 'var(--text-main)',
                        borderBottom: '1px solid rgba(15,23,42,0.06)',
                        padding: '13px 0'
                      }}
                    >
                      {link.name}
                      {isActive && (
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-magenta)', flexShrink: 0 }} />
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* Bottom CTA */}
              <div style={{ padding: '20px', borderTop: '1px solid var(--color-border)' }}>
                <a
                  href={generateGeneralWhatsAppLink('International Buyer')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ width: '100%', justifyContent: 'center', marginBottom: '10px' }}
                >
                  EXPORT ENQUIRY <ArrowUpRight size={15} />
                </a>
                <a
                  href={`tel:${company.phone}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '7px', fontSize: '13px', fontWeight: 700, color: 'var(--text-muted)',
                    padding: '8px 0'
                  }}
                >
                  <Phone size={13} /> {company.phone}
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Responsive CSS ── */}
      <style>{`
        @media (max-width: 1160px) {
          .desktop-nav { gap: 2px !important; }
          .desktop-nav a { padding: 6px 10px !important; font-size: 12.5px !important; }
        }
        @media (max-width: 980px) {
          .desktop-nav { display: none !important; }
          .desktop-cta { display: none !important; }
          .mobile-menu-toggle { display: flex !important; }
        }
        @media (max-width: 768px) {
          .navbar-logo {
            height: 48px !important;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;
